import { Add } from "@mui/icons-material";
import { Fab, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { GqlPermissions } from "~/__generated__";
import { useProjectExportDelete } from "~/hooks/useProjectExportDelete";
import { useProjectExports } from "~/hooks/useProjectExports";
import GaiaConfirmActionDialog from "~/modules/app/components/GaiaConfirmActionDialog";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import useQueryStringGridModels from "~/modules/app/hooks/useQueryStringGridModels";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import { convertParamsToQueryOptions } from "~/modules/app/util/filterAndPaginationUtils";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";
import useRequireCurrentClient from "~/modules/client/hooks/useRequireCurrentClient";
import { ProjectExportTable } from "~/shared/components/ProjectExportTable";

import useRequireCurrentProject from "../hooks/useRequireCurrentProject";

const DEFAULT_PARAMS = {
    page: 0,
    pageSize: 50,
    sortBy: "createdAt",
    sortOrder: "asc",
};

export function ProjectExportListScreen() {
    const { client } = useRequireCurrentClient();
    const { project } = useRequireCurrentProject();
    const { t } = useTranslation("projectExport");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);

    const { filterModel, onFilterModelChange, gridModels, gqlVariables } = useQueryStringGridModels(
        convertParamsToQueryOptions,
        DEFAULT_PARAMS,
    );

    const { data, loading, refetch, error, filterOptions, totalRows } = useProjectExports(client.id, {
        ...gqlVariables.params,
        projectId: project.id,
    });

    const deleteAction = useProjectExportDelete();
    const handleDeleteClick = isAdmin ? (elementId: string) => deleteAction.deleteProjectExport(elementId) : undefined;

    if (!data && !loading && error) {
        return <ErrorScreen error={error} />;
    }

    const createExportTooltip = (
        <Tooltip title={t("ProjectExportListScreen.createButton")}>
            <Fab component={Link} to="create" color="primary">
                <Add />
            </Fab>
        </Tooltip>
    );

    return (
        <>
            <GaiaConfirmActionDialog
                blocker={deleteAction.blocker}
                title={t("ProjectExportListScreen.deleteDialog.title")}
                description={t("ProjectExportListScreen.deleteDialog.description")}
            />
            <GaiaPageLayoutColumn maxWidth="xl">
                <GaiaPageGridComponent
                    title={t("ProjectExportListScreen.title")}
                    // TODO: re-enable export page
                    actions={createExportTooltip}
                >
                    <ProjectExportTable
                        data={data ?? []}
                        error={error}
                        filterModel={filterModel}
                        filterOptions={filterOptions}
                        gridModels={gridModels}
                        loading={loading}
                        onDeleteClick={handleDeleteClick}
                        onFilterModelChange={onFilterModelChange}
                        onRefreshRequest={refetch}
                        total={totalRows ?? 0}
                    />
                </GaiaPageGridComponent>
            </GaiaPageLayoutColumn>
        </>
    );
}
